/**
 * Adapting converted glTF geometry to what the ported shaders expect.
 *
 * The shaders are a faithful port and keep the original's attribute names and layout
 * (`simple.vert`: position, normal, texcoord, tangent, bitangent). glTF uses different
 * names and packs handedness into the tangent's fourth component. Rather than bend the
 * shaders — which are verified against the original — the glue lives here.
 *
 * The converter also records each material's original texture paths in the glTF `extras`
 * rather than embedding images, so textures resolve through the asset manifest by their
 * original engine path. See tools/convert/meshes.py.
 */

import {
  BufferGeometry,
  Float32BufferAttribute,
  Mesh,
  Vector3,
  type BufferAttribute,
  type InterleavedBufferAttribute,
} from 'three';
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';

export interface MaterialInfo {
  /**
   * Original engine paths, resolved against AssetManifest.textures.
   * Explicitly `| undefined` rather than optional: the converter writes an empty string for
   * an absent map, so "absent" is a value we carry, not a key we omit.
   */
  albedo: string | undefined;
  normal: string | undefined;
  specular: string | undefined;
  illumination: string | undefined;
  /** simple.frag `materialparams`: x = shininess, y = specular amount, z = self-illumination. */
  shininess: number;
  specularAmount: number;
  selfIllumination: number;
}

export interface DrawItem {
  geometry: BufferGeometry;
  material: MaterialInfo;
  name: string;
}

function readExtras(source: unknown): Record<string, unknown> {
  return typeof source === 'object' && source !== null
    ? (source as Record<string, unknown>)
    : {};
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function num(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/**
 * Rename and synthesise attributes in place.
 *
 * `uv` -> `texcoord`: the shader declares vec3 and the buffer supplies vec2; the third
 * component defaults to 0 and the shader only ever reads `.st`.
 *
 * `bitangent` is computed rather than stored. glTF encodes handedness in tangent.w and
 * leaves the bitangent derivable, which is exactly what the converter relied on — note
 * that the original stored both and used a LEFT-handed convention, while the converted
 * data uses glTF's rule. Getting this backwards inverts normal-map lighting.
 */
function adaptAttributes(geometry: BufferGeometry): BufferGeometry {
  const uv = geometry.getAttribute('uv');
  if (uv && !geometry.getAttribute('texcoord')) {
    geometry.setAttribute('texcoord', uv);
  }

  const normal = geometry.getAttribute('normal');
  const tangent = geometry.getAttribute('tangent');

  if (normal && tangent && !geometry.getAttribute('bitangent')) {
    geometry.setAttribute('bitangent', computeBitangents(normal, tangent));
  }

  return geometry;
}

function computeBitangents(
  normal: BufferAttribute | InterleavedBufferAttribute,
  tangent: BufferAttribute | InterleavedBufferAttribute,
): Float32BufferAttribute {
  const count = normal.count;
  const out = new Float32Array(count * 3);
  const n = new Vector3();
  const t = new Vector3();
  const b = new Vector3();

  for (let i = 0; i < count; i++) {
    n.set(normal.getX(i), normal.getY(i), normal.getZ(i));
    t.set(tangent.getX(i), tangent.getY(i), tangent.getZ(i));
    // glTF: bitangent = cross(normal, tangent.xyz) * tangent.w
    const w = tangent.itemSize >= 4 ? tangent.getW(i) : 1;
    b.crossVectors(n, t).multiplyScalar(w);
    out[i * 3] = b.x;
    out[i * 3 + 1] = b.y;
    out[i * 3 + 2] = b.z;
  }

  return new Float32BufferAttribute(out, 3);
}

/** Flatten a loaded glTF into draw items the deferred pipeline can consume. */
export function toDrawItems(gltf: GLTF): DrawItem[] {
  const items: DrawItem[] = [];

  gltf.scene.traverse((object) => {
    if (!(object instanceof Mesh)) return;

    const extras = readExtras(
      (object.material as { userData?: unknown } | undefined)?.userData,
    );

    items.push({
      name: object.name || 'unnamed',
      geometry: adaptAttributes(object.geometry as BufferGeometry),
      // Key names verified against the emitted glTF, not assumed. Absent maps are the
      // empty string rather than a missing key, which is why str() rejects "".
      material: {
        albedo: str(extras['diffuseTexture']),
        normal: str(extras['normalTexture']),
        specular: str(extras['specularTexture']),
        illumination: str(extras['illuminationTexture']),
        shininess: num(extras['shininess'], 0),
        specularAmount: num(extras['specularAmount'], 0),
        selfIllumination: num(extras['selfIllumination'], 0),
      },
    });
  });

  return items;
}
